import * as Tone from "tone";
import {
    InstrumentName,
    Note,
    InstrumentInfo,
    PlaybackOptions,
    IntervalPlaybackOptions,
    SoundLibraryConfig,
    LoadProgressCallback,
    SoundLibraryError,
} from "../types/sound";
import { instrumentSamples } from "../data/instrumentSamples";
import { noteToMidi, sortNotes } from "../utils/noteUtils";

export class SoundLibrary {
    private samplers: Map<InstrumentName, Tone.Sampler> = new Map();
    private loadedInstruments: Set<InstrumentName> = new Set();
    private loadingInstruments: Set<InstrumentName> = new Set();
    private config: Required<SoundLibraryConfig>;
    private audioStarted: boolean = false;
    private aborted: boolean = false;
    private activeTimeouts: Set<NodeJS.Timeout> = new Set();
    private masterCompressor: Tone.Compressor | null = null;
    private masterLimiter: Tone.Limiter | null = null;

    constructor(config: SoundLibraryConfig = {}) {
        this.config = {
            baseUrl: process.env.PUBLIC_URL || "",
            enableCaching: true,
            maxCacheSize: 10,
            loadTimeout: 10000,
            ...config,
        };
    }

    private ensureMasterEffects(): void {
        if (!this.masterCompressor || !this.masterLimiter) {
            // Create a dynamics compressor to smooth out volume peaks
            // This handles the rapid note presses by reducing gain when signal gets loud
            // Very gentle compression - mostly transparent to volume slider changes
            // Threshold at -3dB compresses only peak signals, leaving normal range untouched
            // 1.5:1 ratio is minimal compression for natural, uncolored sound
            this.masterCompressor = new Tone.Compressor({
                threshold: -3,      // Only compress peaks at -3dB, leaving most signals uncompressed
                ratio: 1.5,         // 1.5:1 compression ratio - very gentle, almost transparent
                attack: 0.003,      // Fast attack (3ms) to catch transients
                release: 0.2,       // Longer release (200ms) for very smooth response
                knee: 1,            // Minimal knee for precise control
            });

            // Create a brick-wall limiter as final safety to prevent any clipping
            // This catches anything that gets through the compressor
            this.masterLimiter = new Tone.Limiter(0).toDestination();

            // Chain: Compressor -> Limiter -> Destination
            this.masterCompressor.connect(this.masterLimiter);
        }
    }

    async ensureAudioContext(): Promise<void> {
        if (!this.audioStarted) {
            await Tone.start();
            this.audioStarted = true;
        }
    }

    async loadInstrument(
        instrument: InstrumentName,
        onProgress?: LoadProgressCallback
    ): Promise<void> {
        if (this.loadedInstruments.has(instrument)) {
            return;
        }

        if (this.loadingInstruments.has(instrument)) {
            return new Promise((resolve) => {
                const checkLoaded = () => {
                    if (this.loadedInstruments.has(instrument)) {
                        resolve();
                    } else {
                        setTimeout(checkLoaded, 100);
                    }
                };
                checkLoaded();
            });
        }

        this.loadingInstruments.add(instrument);

        try {
            await this.ensureAudioContext();
            this.ensureMasterEffects();  // Create master effects after audio context is ready

            const samples = instrumentSamples[instrument];
            if (!samples) {
                throw this.createError(
                    "INSTRUMENT_NOT_FOUND",
                    `Instrument ${instrument} not found`,
                    instrument
                );
            }

            const sampleUrls = Object.fromEntries(
                Object.entries(samples).map(([note, path]) => [
                    note,
                    `${this.config.baseUrl}${path}`,
                ])
            );

            const totalSamples = Object.keys(sampleUrls).length;
            let loadedSamples = 0;

            const sampler = new Tone.Sampler({
                urls: sampleUrls,
                onload: () => {
                    this.samplers.set(instrument, sampler);
                    this.loadedInstruments.add(instrument);
                    this.loadingInstruments.delete(instrument);

                    if (onProgress) {
                        onProgress({
                            loaded: totalSamples,
                            total: totalSamples,
                            instrument,
                            isComplete: true,
                        });
                    }
                },
                onerror: (error) => {
                    this.loadingInstruments.delete(instrument);
                    throw this.createError(
                        "LOAD_ERROR",
                        `Failed to load ${instrument}`,
                        instrument,
                        error
                    );
                },
            }).connect(this.masterCompressor!);  // Route through compressor->limiter chain


            if (onProgress) {
                const progressInterval = setInterval(() => {
                    loadedSamples = Math.min(loadedSamples + 1, totalSamples - 1);
                    onProgress({
                        loaded: loadedSamples,
                        total: totalSamples,
                        instrument,
                        isComplete: false,
                    });

                    if (this.loadedInstruments.has(instrument)) {
                        clearInterval(progressInterval);
                    }
                }, 100);
            }

            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    this.loadingInstruments.delete(instrument);
                    reject(
                        this.createError("LOAD_ERROR", `Timeout loading ${instrument}`, instrument)
                    );
                }, this.config.loadTimeout);

                const checkLoaded = () => {
                    if (this.loadedInstruments.has(instrument)) {
                        clearTimeout(timeout);
                        resolve();
                    } else {
                        setTimeout(checkLoaded, 100);
                    }
                };
                checkLoaded();
            });
        } catch (error) {
            this.loadingInstruments.delete(instrument);
            throw error;
        }
    }

    async loadMultipleInstruments(
        instruments: InstrumentName[],
        onProgress?: LoadProgressCallback
    ): Promise<void> {
        const totalInstruments = instruments.length;
        let completedInstruments = 0;

        const loadPromises = instruments.map(async (instrument) => {
            await this.loadInstrument(instrument, (progress) => {
                if (progress.isComplete) {
                    completedInstruments++;
                    if (onProgress) {
                        onProgress({
                            loaded: completedInstruments,
                            total: totalInstruments,
                            instrument,
                            isComplete: completedInstruments === totalInstruments,
                        });
                    }
                }
            });
        });

        await Promise.all(loadPromises);
    }

    async playNote(
        instrument: InstrumentName,
        note: Note,
        options: PlaybackOptions = {}
    ): Promise<void> {
        const { duration = 1, velocity = 0.8, delay = 0 } = options;

        await this.ensureInstrumentLoaded(instrument);

        const sampler = this.samplers.get(instrument);
        if (!sampler) {
            throw this.createError(
                "INSTRUMENT_NOT_FOUND",
                `Instrument ${instrument} not loaded`,
                instrument
            );
        }

        try {
            const playFunction = () => {
                // Check if aborted before playing
                if (this.aborted) {
                    return Promise.resolve();
                }
                // Let Tone.Sampler handle pitch-shifting automatically
                sampler.triggerAttackRelease(note, duration, undefined, velocity);
                return Promise.resolve();
            };

            if (delay > 0) {
                return new Promise((resolve, reject) => {
                    const timeoutId = setTimeout(async () => {
                        this.activeTimeouts.delete(timeoutId);
                        try {
                            await playFunction();
                            resolve();
                        } catch (err) {
                            reject(err);
                        }
                    }, delay * 1000);
                    this.activeTimeouts.add(timeoutId);
                });
            } else {
                await playFunction();
            }
        } catch (error) {
            throw this.createError(
                "PLAYBACK_ERROR",
                `Failed to play ${note} on ${instrument}`,
                instrument,
                error instanceof Error ? error : undefined,
                note
            );
        }
    }

    async playChord(
        instrument: InstrumentName,
        notes: Note[],
        options: PlaybackOptions = {}
    ): Promise<void> {
        const { duration = 1, velocity = 0.8, delay = 0 } = options;

        await this.ensureInstrumentLoaded(instrument);

        const sampler = this.samplers.get(instrument);
        if (!sampler) {
            throw this.createError(
                "INSTRUMENT_NOT_FOUND",
                `Instrument ${instrument} not loaded`,
                instrument
            );
        }

        try {
            // Normalize velocity based on number of notes to prevent clipping
            // This ensures chords don't clip while maintaining the user's volume intent
            const noteCount = notes.length;
            const normalizedVelocity = velocity / Math.sqrt(noteCount);
            
            // Play each note with normalized velocity
            const notePromises = notes.map((note) =>
                this.playNote(instrument, note, { duration, velocity: normalizedVelocity, delay })
            );

            await Promise.all(notePromises);
        } catch (error) {
            throw this.createError(
                "PLAYBACK_ERROR",
                `Failed to play chord on ${instrument}`,
                instrument,
                error instanceof Error ? error : undefined
            );
        }
    }

    async playInterval(
        instrument: InstrumentName,
        note1: Note,
        note2: Note,
        options: IntervalPlaybackOptions = {}
    ): Promise<void> {
        const {
            duration = 1,
            velocity = 0.8,
            delay = 0,
            simultaneousPlayback = false,
            intervalDelayMs = 500,
        } = options;

        if (simultaneousPlayback) {
            await this.playChord(instrument, [note1, note2], { duration, velocity, delay });
        } else {
            await this.playNote(instrument, note1, { duration, velocity, delay });
            await this.playNote(instrument, note2, {
                duration,
                velocity,
                delay: delay + intervalDelayMs / 1000,
            });
        }
    }

    async playSequence(
        instrument: InstrumentName,
        notes: Note[],
        options: PlaybackOptions & { noteDelayMs?: number } = {}
    ): Promise<void> {
        const { duration = 1, velocity = 0.8, delay = 0, noteDelayMs = 500 } = options;

        for (let i = 0; i < notes.length; i++) {
            if (this.aborted) {
                this.aborted = false;
                return;
            }
            
            const noteDelay = delay + (i * noteDelayMs) / 1000;
            this.playNote(instrument, notes[i], { duration, velocity, delay: noteDelay });
        }
        
        const totalDuration = delay + ((notes.length - 1) * noteDelayMs) / 1000 + duration;
        await new Promise(resolve => setTimeout(resolve, totalDuration * 1000));
    }

    getInstrumentInfo(instrument: InstrumentName): InstrumentInfo {
        const samples = instrumentSamples[instrument];
        if (!samples) {
            throw this.createError(
                "INSTRUMENT_NOT_FOUND",
                `Instrument ${instrument} not found`,
                instrument
            );
        }

        const availableNotes = Object.keys(samples) as Note[];
        const sortedNotes = sortNotes(availableNotes);

        return {
            name: instrument,
            samples,
            availableNotes: sortedNotes,
            range: {
                lowest: sortedNotes[0],
                highest: sortedNotes[sortedNotes.length - 1],
            },
        };
    }

    getAvailableInstruments(): InstrumentName[] {
        return Object.keys(instrumentSamples) as InstrumentName[];
    }

    isInstrumentLoaded(instrument: InstrumentName): boolean {
        return this.loadedInstruments.has(instrument);
    }

    isNoteAvailable(instrument: InstrumentName, note: Note): boolean {
        const samples = instrumentSamples[instrument];
        return samples ? note in samples : false;
    }

    getClosestAvailableNote(instrument: InstrumentName, targetNote: Note): Note | null {
        const info = this.getInstrumentInfo(instrument);
        const targetMidi = noteToMidi(targetNote);

        let closestNote: Note | null = null;
        let smallestDistance = Infinity;

        for (const note of info.availableNotes) {
            const noteMidi = noteToMidi(note);
            const distance = Math.abs(noteMidi - targetMidi);

            if (distance < smallestDistance) {
                smallestDistance = distance;
                closestNote = note;
            }
        }

        return closestNote;
    }

    getRandomNote(instrument: InstrumentName): Note {
        const info = this.getInstrumentInfo(instrument);
        const randomIndex = Math.floor(Math.random() * info.availableNotes.length);
        return info.availableNotes[randomIndex];
    }

    getRandomNoteInRange(instrument: InstrumentName, minNote?: Note, maxNote?: Note): Note {
        const info = this.getInstrumentInfo(instrument);

        const actualMin = minNote || info.range.lowest;
        const actualMax = maxNote || info.range.highest;

        const minMidi = noteToMidi(actualMin);
        const maxMidi = noteToMidi(actualMax);

        const availableInRange = info.availableNotes.filter((note) => {
            const midi = noteToMidi(note);
            return midi >= minMidi && midi <= maxMidi;
        });

        if (availableInRange.length === 0) {
            throw this.createError(
                "INVALID_NOTE",
                `No notes available in range ${actualMin}-${actualMax} for ${instrument}`,
                instrument
            );
        }

        const randomIndex = Math.floor(Math.random() * availableInRange.length);
        return availableInRange[randomIndex];
    }

    unloadInstrument(instrument: InstrumentName): void {
        const sampler = this.samplers.get(instrument);
        if (sampler) {
            sampler.dispose();
            this.samplers.delete(instrument);
            this.loadedInstruments.delete(instrument);
        }
    }

    unloadAllInstruments(): void {
        Array.from(this.loadedInstruments).forEach((instrument) => {
            this.unloadInstrument(instrument);
        });
    }

    dispose(): void {
        this.unloadAllInstruments();
        
        // Clean up master effects
        if (this.masterLimiter) {
            this.masterLimiter.dispose();
        }
    }

    stopAll(): void {
        // Abort any ongoing sequences
        this.aborted = true;
        
        // Cancel all pending timeouts
        this.activeTimeouts.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        this.activeTimeouts.clear();
        
        // Stop all currently playing sounds
        this.samplers.forEach((sampler) => {
            sampler.releaseAll();
        });
        
        // Reset abort flag immediately - component-level refs will handle their own abort logic
        this.aborted = false;
    }

    clearAbort(): void {
        this.aborted = false;
    }

    private async ensureInstrumentLoaded(instrument: InstrumentName): Promise<void> {
        if (!this.isInstrumentLoaded(instrument)) {
            await this.loadInstrument(instrument);
        }
    }

    private createError(
        code: SoundLibraryError["code"],
        message: string,
        instrument?: InstrumentName,
        originalError?: Error,
        note?: Note
    ): SoundLibraryError {
        return {
            code,
            message,
            instrument,
            note,
            originalError,
        };
    }
}
